import styled from '@emotion/styled/macro';
import {
  Banner,
  Card,
  Modal,
  Snackbar,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  sendProposalEmail,
  sendProposalMessageAndEmail,
} from 'providers/ShareProposal';

const StyledBanner = styled(Banner)`
  &.sr-banner {
    border: none;
    margin-top: 0;
    margin-bottom: 16px;
  }
`;

type SendStatus = 'idle' | 'pending' | 'success' | 'error' | 'sessionExpired';

const SESSION_EXPIRED_COPY =
  'Your session has expired. Refresh the page to sign in again, then try sending the bill.';

const getErrorCopy = (status: SendStatus): string | null => {
  if (status === 'sessionExpired') {
    return SESSION_EXPIRED_COPY;
  }
  if (status === 'error') {
    return 'Error sending email';
  }
  return null;
};

type ShareBillModalProps = {
  hybridToken: string;
  onDismiss: () => void;
  prospectId: string;
  name: string;
  email: string;
  isAffiliate?: boolean;
};

const ShareBillModal = ({
  hybridToken,
  onDismiss,
  prospectId,
  name,
  email,
  isAffiliate,
}: ShareBillModalProps) => {
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');

  useEffect(() => {
    setSendStatus('idle');
  }, [prospectId, isAffiliate]);

  const handleDismiss = useCallback(() => {
    setSendStatus('idle');
    onDismiss();
  }, [onDismiss]);

  const handleSend = useCallback(async () => {
    setSendStatus('pending');
    try {
      if (isAffiliate) {
        await sendProposalEmail(hybridToken, prospectId, '', 'UTILITY_WELCOME');
      } else {
        await sendProposalMessageAndEmail(
          hybridToken,
          prospectId,
          '',
          'UTILITY_WELCOME',
        );
      }
      setSendStatus('success');
    } catch (error) {
      const statusCode = (error as { response?: { statusCode?: number } })
        .response?.statusCode;
      setSendStatus(statusCode === 401 ? 'sessionExpired' : 'error');
    }
  }, [hybridToken, isAffiliate, prospectId]);

  const bannerText = isAffiliate
    ? `${name} can download the Sunrun Customer App to view their offers and track their project.`
    : `${name} should download the Sunrun Customer App to receive & send messages, view their offers, and track their project.`;
  const actionButtonText = isAffiliate
    ? 'Share by email'
    : 'Share by email & messages';
  const isPending = sendStatus === 'pending';
  const sendErrorCopy = getErrorCopy(sendStatus);

  if (sendStatus === 'success') {
    return createPortal(
      <Snackbar
        open
        onClose={handleDismiss}
        message="Bill request sent successfully!"
        type="success"
        colorScheme="dark"
      />,
      document.body,
    );
  }

  return createPortal(
    <Modal
      onClose={handleDismiss}
      testId="share-bill-modal"
      title="Ask customer to share bill"
      subtitle="Sending the offer presentation will allow the customer to upload their utility bill."
      customModalWidth={820}
      secondaryButton={{
        text: 'Cancel',
        onClick: handleDismiss,
        testId: 'share-bill-modal-cancel-button',
        disabled: isPending,
      }}
      primaryButton={{
        text: actionButtonText,
        onClick: handleSend,
        testId: 'share-bill-modal-send-email-button',
        isLoading: isPending,
        disabled: isPending,
      }}>
      <StyledBanner show={true} showClose={false}>
        {bannerText}
      </StyledBanner>
      <Card>
        <Typography variant="p">{name}</Typography>
        <Typography variant="p" color={tokens.TINTS_OFF_WHITE_20}>
          {email}
        </Typography>
      </Card>
      {sendErrorCopy && (
        <Typography variant="p" color={tokens.TINTS_ERROR_20}>
          {sendErrorCopy}
        </Typography>
      )}
    </Modal>,
    document.body,
  );
};

export { ShareBillModal };
